/**
 * Orca v3 — a saved loop and its definition, as stored in `orcaloops_v3.loops` and
 * `orcaloops_v3.loop_versions`.
 *
 * A loop is an outcome someone wants, stated as conditions that must all hold, plus the
 * inputs a run needs, the actions around the work, a budget, and what agents may touch.
 * Nothing here names a particular outside system: GitHub, Linear and the rest appear only
 * as connector ids in `connector` fields, resolved against the catalog in `@fm/v3`.
 *
 * These schemas are the single source of truth for the persisted JSON (`definition`
 * jsonb); `@fm/v3` re-exports them without the `V3` prefix.
 *
 * @since 1.0.0
 */
import * as Model from "@effect/sql/Model";
import { Option, Schema } from "effect";

/**
 * Who decides a condition. A condition is never decided by the agent doing the work.
 *
 * - `runtime` — a command the runtime runs itself (the repo's checks, a clean merge).
 * - `reviewer` — a fresh agent that never saw the work.
 * - `person` — a human answer.
 * - `connector` — the state of an outside system, read through its API.
 *
 * @since 1.0.0
 */
export const V3Decider = Schema.Literal("runtime", "reviewer", "person", "connector");
export type V3Decider = typeof V3Decider.Type;

/**
 * Free-form parameters of a condition or action, validated against the catalog entry for
 * its kind by `@fm/v3`'s `validateLoopDefinition`.
 *
 * @since 1.0.0
 */
export const V3Params = Schema.Record({ key: Schema.String, value: Schema.Unknown });
export type V3Params = typeof V3Params.Type;

/**
 * One input a run needs. `text` is typed by the person; `connector` is fetched from an
 * outside system (a Linear issue used as the brief, a Figma frame) and snapshotted at
 * run start, so a run never depends on that system staying reachable.
 *
 * @since 1.0.0
 */
export const V3InputSpec = Schema.Struct({
  /** Stable key a run's `inputs` record uses, e.g. `brief` or `issue`. */
  id: Schema.String,
  title: Schema.String,
  kind: Schema.Literal("text", "connector"),
  /** Connector id for `kind: "connector"`, e.g. `linear`. */
  connector: Schema.optional(Schema.String),
  /** The connector's input kind, e.g. `issue`. */
  input: Schema.optional(Schema.String),
  required: Schema.Boolean,
  description: Schema.optional(Schema.String),
});
export type V3InputSpec = typeof V3InputSpec.Type;

/**
 * The code workspace a run works in, hosted by a connector that provides a git remote.
 *
 * @since 1.0.0
 */
export const V3WorkspaceSpec = Schema.Struct({
  connector: Schema.String,
  /** The connector's repository handle, e.g. `owner/name`. */
  repo: Schema.String,
  /** The branch changes merge into. */
  base: Schema.String,
});
export type V3WorkspaceSpec = typeof V3WorkspaceSpec.Type;

/**
 * One condition of the outcome.
 *
 * @since 1.0.0
 */
export const V3ConditionSpec = Schema.Struct({
  /** Unique within the loop; what events and the run page key on. */
  id: Schema.String,
  /** Catalog kind, e.g. `workspace.checks`, `review.code`, `change_request.ci_green`. */
  kind: Schema.String,
  title: Schema.optional(Schema.String),
  by: V3Decider,
  /** Required when `by` is `connector`. */
  connector: Schema.optional(Schema.String),
  params: Schema.optional(V3Params),
  /** Conditions that must hold on the same subject before this one is checked. */
  after: Schema.optional(Schema.Array(Schema.String)),
});
export type V3ConditionSpec = typeof V3ConditionSpec.Type;

/**
 * How the runtime confirms an action took effect, by re-reading the system it acted on.
 *
 * @since 1.0.0
 */
export const V3ActionVerify = Schema.Struct({
  /** A condition kind of the same connector, e.g. `change_request.merged`. */
  kind: Schema.String,
  params: Schema.optional(V3Params),
});
export type V3ActionVerify = typeof V3ActionVerify.Type;

/**
 * One action the runtime takes around the work: `before` the orchestrator starts, `after`
 * every condition holds, or `ifUnmet` when a run stops, fails or runs out of budget.
 *
 * A `required` step that fails sends the run back to work (after) or stops it (before).
 * A step that is not required is best-effort and never blocks. Linear steps default to
 * not required.
 *
 * @since 1.0.0
 */
export const V3ActionStep = Schema.Struct({
  connector: Schema.String,
  /** Catalog action id, e.g. `change_request.merge`, `issue.set_state`, `issue.comment`. */
  action: Schema.String,
  params: Schema.optional(V3Params),
  required: Schema.Boolean,
  verify: Schema.optional(V3ActionVerify),
});
export type V3ActionStep = typeof V3ActionStep.Type;

/**
 * Money and time ceilings for one run. The runtime refuses new work past either.
 *
 * @since 1.0.0
 */
export const V3Budget = Schema.Struct({
  usd: Schema.Number,
  minutes: Schema.Number,
  /** Ceiling for one subagent; defaults to the remaining run budget. */
  perAgentUsd: Schema.optional(Schema.Number),
});
export type V3Budget = typeof V3Budget.Type;

/**
 * What agents may reach through one connector. Agents never hold the credential: the
 * loop host proxies the vendor's MCP server (read-only unless stated) and runs the listed
 * actions for the orchestrator's `take_action` tool.
 *
 * @since 1.0.0
 */
export const V3AgentAccess = Schema.Struct({
  connector: Schema.String,
  mcp: Schema.optional(Schema.Struct({
    readonly: Schema.Boolean,
    toolsets: Schema.Array(Schema.String),
  })),
  /**
   * Catalog actions the orchestrator may take, e.g. `change_request.open`. Non-destructive
   * ones always; a destructive one only when its catalog kind has a `grantLimit` and
   * `limits` pins that param, e.g. `issue.set_state` limited to `name: ["Ready for v3"]`.
   */
  actions: Schema.Array(Schema.String),
  /**
   * Per granted action, the only values each param may take: action → param → allowed
   * values, e.g. `{ "issue.set_state": { name: ["Ready for v3"] } }`.
   */
  limits: Schema.optional(Schema.Record({
    key: Schema.String,
    value: Schema.Record({ key: Schema.String, value: Schema.Array(Schema.String) }),
  })),
});
export type V3AgentAccess = typeof V3AgentAccess.Type;

/**
 * An outside event that starts a run of the loop by itself, e.g. a Linear issue entering
 * a workflow state (`{ connector: "linear", on: "issue.state", params: { state } }`), or a
 * GitHub event on the workspace's repo: `change_request.updated` (a pull request opened,
 * reopened or given a new head) or `branch.pushed` (a branch moved to a new head).
 *
 * `on` and `params` stay free-form here so saved loops always decode; `@fm/v3`'s catalog
 * validates them against the connector's trigger kinds.
 *
 * @since 1.0.0
 */
export const V3TriggerSpec = Schema.Struct({
  connector: Schema.String,
  /** The connector's event, e.g. `issue.state` or `change_request.updated`. */
  on: Schema.String,
  params: Schema.optional(V3Params),
});
export type V3TriggerSpec = typeof V3TriggerSpec.Type;

/** `build` (absent): an orchestrator and subagents change the workspace. `verify`: no agents; the host decides the runtime conditions on the run's commit. */
export const V3LoopMode = Schema.Literal("build", "verify");
export type V3LoopMode = typeof V3LoopMode.Type;

/**
 * Everything a run of the loop needs, frozen per version: the catalog format every runtime
 * executes. It never carries `format`, so a {@link V3FlexibleLoopDefinition} can never be
 * decoded as one by dropping its discriminator.
 *
 * @since 1.0.0
 */
export const V3LoopDefinition = Schema.Struct({
  /** Absent: only a flexible definition names its format. */
  format: Schema.optional(Schema.Never),
  inputs: Schema.Array(V3InputSpec),
  workspace: Schema.optional(V3WorkspaceSpec),
  /** Ordered: cheaper and more mechanical conditions first. */
  conditions: Schema.Array(V3ConditionSpec),
  before: Schema.Array(V3ActionStep),
  after: Schema.Array(V3ActionStep),
  ifUnmet: Schema.Array(V3ActionStep),
  budget: V3Budget,
  /** Most subagents at once. */
  parallel: Schema.Number,
  /** Guidance for the orchestrator: how this kind of work is best split and checked. */
  playbook: Schema.optional(Schema.String),
  access: Schema.Array(V3AgentAccess),
  /** Events that start a run by themselves; absent, runs start only by hand. */
  triggers: Schema.optional(Schema.Array(V3TriggerSpec)),
  /** Absent means `build`. */
  mode: Schema.optional(V3LoopMode),
});
export type V3LoopDefinition = typeof V3LoopDefinition.Type;

/**
 * Authored text, stored verbatim: never trimmed or rewritten, only refused when blank.
 *
 * @since 1.0.0
 */
export const V3Text = Schema.String.pipe(
  Schema.filter((text) => text.trim() !== "", { message: () => "must not be blank" }),
  Schema.maxLength(20_000),
);

/**
 * What a flexible definition's conditions and resources are named and referenced by.
 *
 * @since 1.0.0
 */
export const V3FlexibleRef = Schema.String.pipe(
  Schema.pattern(/^[a-z0-9][a-z0-9_-]{0,63}$/, {
    message: () => "must be 1 to 64 lowercase letters, digits, \"-\" or \"_\", starting with a letter or digit",
  }),
);

/**
 * Context a condition's executor is given besides its instructions: a note written into the
 * definition, a run input, or a declared loop resource (optionally one part of it).
 *
 * @since 1.0.0
 */
export const V3ConditionContext = Schema.Union(
  Schema.Struct({ source: Schema.Literal("note"), text: V3Text }),
  Schema.Struct({ source: Schema.Literal("input"), input: Schema.String, note: Schema.optional(V3Text) }),
  Schema.Struct({ source: Schema.Literal("resource"), resource: V3FlexibleRef, detail: Schema.optional(V3Text) }),
);
export type V3ConditionContext = typeof V3ConditionContext.Type;

/**
 * An agent is the condition's primary executor. It may still use scripts and tools while it works.
 *
 * @since 1.0.0
 */
export const V3AgentExecutor = Schema.Struct({
  type: Schema.Literal("agent"),
  /** Why an agent, not a script, should own this condition. */
  rationale: V3Text,
  /** Scripts or tools the agent may use, by name or command. */
  tools: Schema.optional(Schema.Array(V3Text)),
});
export type V3AgentExecutor = typeof V3AgentExecutor.Type;

/**
 * The interpreter a pinned script runs under, inside the host image: a name from this list,
 * never a path or an image the definition chooses.
 *
 * @since 1.0.0
 */
export const V3ScriptInterpreter = Schema.Literal("sh", "bash", "python3", "node", "bun");
export type V3ScriptInterpreter = typeof V3ScriptInterpreter.Type;

const V3Sha256 = Schema.String.pipe(
  Schema.pattern(/^[0-9a-f]{64}$/, { message: () => "must be the lowercase hex SHA-256 of the content" }),
);

/**
 * One reviewed dependency of a pinned script — a module, data file or configuration it reads —
 * stored verbatim beside it and pinned by its own SHA-256. It is placed read-only at its relative
 * `path` next to the script; nothing is installed or fetched to satisfy a dependency.
 *
 * @since 1.0.0
 */
export const V3ScriptFile = Schema.Struct({
  /** Relative to the script's directory, e.g. `lib/totals.py`. */
  path: Schema.String.pipe(Schema.maxLength(200)),
  content: Schema.String.pipe(Schema.maxLength(65_536)),
  sha256: V3Sha256,
});
export type V3ScriptFile = typeof V3ScriptFile.Type;

/**
 * The reviewed script itself, pinned by the SHA-256 of its UTF-8 content, with its reviewed
 * dependency files. All of it is stored verbatim in the loop's immutable version, and every digest is
 * checked at preview, at save and by the host right before the script runs, so what runs — script
 * and dependencies — is exactly what was reviewed. The interpreter comes from the host image; a
 * dependency that is not one of these files (a package to install or fetch) is not supported.
 *
 * @since 1.0.0
 */
export const V3ScriptSource = Schema.Struct({
  interpreter: V3ScriptInterpreter,
  content: Schema.String.pipe(Schema.minLength(1), Schema.maxLength(65_536)),
  sha256: V3Sha256,
  /** Its reviewed dependency files, each pinned; at most 16, 256 KiB with the script. */
  files: Schema.optional(Schema.Array(V3ScriptFile).pipe(Schema.maxItems(16))),
});
export type V3ScriptSource = typeof V3ScriptSource.Type;

/**
 * A script is the condition's primary executor, run without a shell: either the M1 unpinned
 * `command` (authored and reviewed, never executed) or a pinned `source` with literal `args`.
 *
 * @since 1.0.0
 */
export const V3ScriptExecutor = Schema.Struct({
  type: Schema.Literal("script"),
  /** Why a script, not an agent, should own this condition. */
  rationale: V3Text,
  /** An unpinned argv, program first. No runtime executes it: pin a `source` instead. */
  command: Schema.optional(Schema.NonEmptyArray(Schema.String)),
  /** The pinned script that runs. */
  source: Schema.optional(V3ScriptSource),
  /** Literal arguments after the script, passed as argv: never interpolated or expanded. */
  args: Schema.optional(Schema.Array(Schema.String.pipe(Schema.maxLength(4096))).pipe(Schema.maxItems(64))),
  /** The declared resource the command runs in; one of the condition's own resources. */
  workdir: Schema.optional(V3FlexibleRef),
  timeoutMinutes: Schema.Number.pipe(Schema.int(), Schema.positive()),
  /** The most output kept, in bytes, after redaction: 1 KiB to 64 KiB, 16 KiB when absent. */
  outputLimitBytes: Schema.optional(Schema.Number.pipe(Schema.int(), Schema.between(1024, 65_536))),
});
export type V3ScriptExecutor = typeof V3ScriptExecutor.Type;

/**
 * Exactly one primary executor per condition.
 *
 * @since 1.0.0
 */
export const V3ConditionExecutor = Schema.Union(V3AgentExecutor, V3ScriptExecutor);
export type V3ConditionExecutor = typeof V3ConditionExecutor.Type;

/**
 * One loop-specific condition: enough instructions, context and observable success criteria for
 * an executor to turn it into work. It is not a catalog kind.
 *
 * @since 1.0.0
 */
export const V3FlexibleCondition = Schema.Struct({
  /** Unique within the loop. */
  id: V3FlexibleRef,
  title: V3Text,
  instructions: V3Text,
  context: Schema.optional(Schema.Array(V3ConditionContext)),
  /** Observable checks that decide whether the condition holds. */
  successCriteria: Schema.NonEmptyArray(V3Text),
  executor: V3ConditionExecutor,
  /** Declared loop resources this condition works with. */
  resources: Schema.optional(Schema.Array(V3FlexibleRef)),
});
export type V3FlexibleCondition = typeof V3FlexibleCondition.Type;

/**
 * A named execution resource the loop owns across invocations: its reusable identity and
 * non-secret configuration, never provisioned state. Unrelated to the polled `resource_targets`.
 *
 * @since 1.0.0
 */
export const V3LoopResource = Schema.Struct({
  /** Unique within the loop; conditions reference it. */
  id: V3FlexibleRef,
  title: V3Text,
  /** What it is, in the author's words, e.g. `git repository` or `spreadsheet`. */
  kind: V3Text,
  description: Schema.optional(V3Text),
  /** Where it lives, e.g. `acme/api` or a URL. Absent until known. */
  locator: Schema.optional(V3Text),
  provider: Schema.optional(V3Text),
  /** Non-secret settings. */
  config: Schema.optional(
    Schema.Record({ key: Schema.String, value: Schema.Union(Schema.String, Schema.Number, Schema.Boolean) }),
  ),
});
export type V3LoopResource = typeof V3LoopResource.Type;

/**
 * What creation intake inspected: the requirement as stated, what was looked at and found, and
 * what is still open.
 *
 * @since 1.0.0
 */
export const V3LoopIntake = Schema.Struct({
  /** The requirement, verbatim. */
  request: V3Text,
  inspected: Schema.Array(Schema.Struct({ source: V3Text, finding: V3Text })),
  /** Items the person must still supply or decide. */
  open: Schema.optional(Schema.Array(V3Text)),
});
export type V3LoopIntake = typeof V3LoopIntake.Type;

/**
 * A loop stated as its own flexible conditions, resources and intake. Only a person starts it,
 * by hand; each pinned script or agent runs on the host. Only that person pauses or resumes their
 * run of it (a resumed internal run checks every condition again); every trigger and schedule or
 * event binding refuses it.
 *
 * Every decoder refuses fields it does not declare, so a conflicting executor or a secret field
 * is an error, never silently dropped. It lacks the catalog format's required fields, so an
 * older decoder of {@link V3LoopDefinition} fails instead of running something else.
 *
 * @since 1.0.0
 */
export const V3FlexibleLoopDefinition = Schema.Struct({
  format: Schema.Literal("flexible"),
  formatVersion: Schema.Literal(1),
  intake: V3LoopIntake,
  /** Supplied per invocation: `text` inputs. */
  inputs: Schema.Array(V3InputSpec),
  resources: Schema.Array(V3LoopResource),
  conditions: Schema.Array(V3FlexibleCondition),
  budget: V3Budget,
  /** Most conditions worked at once. */
  parallel: Schema.Number,
  /**
   * `proposal`: its person invokes it only from a reviewed intake proposal (`v3.intake.start`), never by a
   * plain start with inputs. Declared in the reviewed definition — no stored loop gains it implicitly — and,
   * like every field, refused by a reader that does not know it: an api that knows nothing of proposals can
   * neither start such a loop nor start its runs, so none runs without its approved refinements.
   */
  invocation: Schema.optional(Schema.Literal("proposal")),
}).annotations({ parseOptions: { onExcessProperty: "error" } });
export type V3FlexibleLoopDefinition = typeof V3FlexibleLoopDefinition.Type;

/**
 * Every definition a loop version can hold: the catalog format, or a flexible one.
 *
 * @since 1.0.0
 */
export const V3StoredLoopDefinition = Schema.Union(V3LoopDefinition, V3FlexibleLoopDefinition);
export type V3StoredLoopDefinition = typeof V3StoredLoopDefinition.Type;

/**
 * Whether a stored definition is a flexible one.
 *
 * @since 1.0.0
 */
export const isFlexibleDefinition = (d: V3StoredLoopDefinition): d is V3FlexibleLoopDefinition =>
  "format" in d && d.format === "flexible";

/**
 * The catalog definition a stored one holds, or none when it is flexible: what every runtime that
 * only executes the catalog format narrows through.
 *
 * @since 1.0.0
 */
export const catalogDefinitionOf = (d: V3StoredLoopDefinition): Option.Option<V3LoopDefinition> =>
  isFlexibleDefinition(d) ? Option.none() : Option.some(d);

/**
 * The head row of a saved loop. Changing `definition` appends a {@link V3LoopVersion}.
 *
 * @since 1.0.0
 */
export class V3Loop extends Model.Class<V3Loop>("V3Loop")({
  id: Model.Generated(Schema.String),
  customerId: Schema.String,
  /** Unique within the customer. */
  slug: Schema.String,
  /** 2–4 capital letters, unique within the customer; null until the api assigns one. */
  key: Schema.NullOr(Schema.String),
  name: Schema.String,
  description: Schema.String,
  /** Used to filter, sort and group the loops list. */
  tags: Schema.Array(Schema.String),
  /** Who wrote the loop. No foreign key — cross-schema by design. */
  authorPersonId: Schema.String,
  version: Schema.Number,
  definition: V3StoredLoopDefinition,
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
  archivedAt: Schema.NullOr(Schema.DateTimeUtc),
}) {}

/**
 * One frozen version of a loop's definition. Runs record the version they ran.
 *
 * @since 1.0.0
 */
export class V3LoopVersion extends Model.Class<V3LoopVersion>("V3LoopVersion")({
  id: Model.Generated(Schema.String),
  loopId: Schema.String,
  customerId: Schema.String,
  version: Schema.Number,
  definition: V3StoredLoopDefinition,
  changeNote: Schema.NullOr(Schema.String),
  authorPersonId: Schema.String,
  createdAt: Model.DateTimeInsertFromDate,
}) {}
